import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Circle, MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import LocationPanel from '../components/LocationPanel';
import Notification from '../components/Notification';
import useGeolocation from '../hooks/useGeolocation';
import api from '../utils/api';
import './MapPage.css';

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});

const mkIcon = color =>
  new L.Icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${color}.png`,
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34]
  });

const icons = { visited: mkIcon('grey'), inZone: mkIcon('gold'), default: mkIcon('orange') };

function MapFollower({ lat, lon }) {
  const map = useMap();

  const didFly = useRef(false);

  useEffect(() => {
    if (lat && lon && !didFly.current) {
      map.flyTo([lat, lon], 16, { duration: 1.5 });

      didFly.current = true;
    }
  }, [lat, lon, map]);
}

const POLL_MS = 10000;

export default function MapPage() {
  const { lat, lon, error: geoError, loading: geoLoading } = useGeolocation();

  const [locations, setLocations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notification, setNotification] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const notified = useRef(new Set());

  const fetchLocations = useCallback(async () => {
    if (!lat || !lon) return;

    try {
      const res = await api.get('/locations/nearby', { params: { lat, lon } });

      setLocations(res.data);

      res.data.forEach(loc => {
        if (loc.in_zone && !loc.visited && !notified.current.has(loc.id)) {
          notified.current.add(loc.id);

          setNotification({ title: `📍 ${loc.name}`, location: loc });
        }
      });
    } catch (_) {}
  }, [lat, lon]);

  useEffect(() => {
    fetchLocations();

    const id = setInterval(fetchLocations, POLL_MS);

    return () => clearInterval(id);
  }, [fetchLocations]);

  const openLocation = async loc => {
    try {
      const res = await api.get(`/locations/${loc.id}`);

      setSelected(res.data);
      setPanelOpen(true);
    } catch (_) {}
  };

  if (geoLoading) return (
    <div className='map-loading'>
      <div className='spinner' />
      <p>Определяем местоположение…</p>
    </div>
  );

  if (geoError) return (
    <div className='map-error'>
      <p>⚠️ {geoError}</p>
      <p className='hint'>Разрешите доступ к геолокации в настройках браузера</p>
    </div>
  );

  const center = lat && lon ? [lat, lon] : [51.533, 46.034];

  return (
    <div className='map-page'>
      {notification && (
        <Notification
          title={notification.title}
          message='Нажмите, чтобы исследовать объект'
          onClose={() => setNotification(null)}
          onClick={() => {
            setNotification(null);
            openLocation(notification.location);
          }}
        />
      )}

      {panelOpen && selected && (
        <LocationPanel
          location={selected}
          userLat={lat}
          userLon={lon}
          onClose={() => { setPanelOpen(false); setSelected(null); }}
          onInteractionComplete={fetchLocations}
        />
      )}

      <MapContainer center={center} zoom={15} className='leaflet-map'>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        />
        <MapFollower lat={lat} lon={lon} />

        {lat && lon && (
          <>
            <Marker position={[lat, lon]}>
              <Popup>
                <div className='popup-content'>
                  <span className='popup-name'>📍 Вы здесь</span>
                </div>
              </Popup>
            </Marker>
            <Circle
              center={[lat, lon]}
              radius={20}
              pathOptions={{ color: '#D4A017', fillColor: '#D4A017', fillOpacity: 0.2 }}
            />
          </>
        )}

        {locations.map(loc => (
          <React.Fragment key={loc.id}>
            <Circle
              center={[loc.latitude, loc.longitude]}
              radius={loc.visit_radius_m}
              pathOptions={{
                color: loc.in_zone ? '#D4A017' : '#8A6D1B',
                fillColor: loc.in_zone ? '#E3B23C' : '#DCC48E',
                fillOpacity: loc.in_zone ? 0.18 : 0.08,
                dashArray: loc.visited ? '6 4' : undefined,
                weight: loc.in_zone ? 2.5 : 1.5
              }}
            />
            <Marker
              position={[loc.latitude, loc.longitude]}
              icon={loc.visited ? icons.visited : loc.in_zone ? icons.inZone : icons.default}
              eventHandlers={{ click: () => openLocation(loc) }}
            >
              <Popup>
                <div className='popup-content'>
                  <span className='popup-name'>{loc.name}</span>
                  <span className='popup-era'>{loc.era}</span>
                  <span className='popup-dist'>{loc.distance_m} м от вас</span>
                  {loc.in_zone && !loc.visited && (
                    <button className='popup-btn' onClick={() => openLocation(loc)}>
                      Исследовать
                    </button>
                  )}
                  {loc.visited && <span className='popup-visited'>✓ Посещено</span>}
                </div>
              </Popup>
            </Marker>
          </React.Fragment>
        ))}
      </MapContainer>
    </div>
  );
}
